/**
 * Add/Edit dialog — bám `frmFuturesQryBankCashInAdd`:
 *   - Bank (read-only), Date
 *   - Type of Cash combobox (Cash In | Cash Out) — gate enable/disable hai cột
 *   - Cash In / Currency In (VND|USD), Cash Out / Currency Out (VND|USD), FX
 *   - Default currency = BIDV ? "USD" : "VND" (theo field DefaultValue Access).
 *   - Edit mode disable Date + chỉ enable nhánh có giá trị.
 *   - Save → upsert; Cancel → close.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '../../../hooks/useSnackbar'
import { useUpsertBankCashMutation } from '../../../store/api/futuresControlApi'
import { FORM_GRID_2COL } from '../../../theme/responsive'
import { futuresApiErrorMessage } from '../futuresApiErrorMessage'
import {
  futuresFormDialogActionBtnSx,
  futuresFormDialogActionsBoxSx,
  futuresFormDialogTitleSx,
  futuresFormDialogTitleTextSx,
  futuresSaleContractDialogContentSx,
  futuresSaleContractDialogPaperProps,
} from '../futuresAccessUi'
import NumericInput from '../NumericInput'

export type BankName = 'Vietinbank' | 'MBbank' | 'BIDV'
type TypeOfCash = 'Cash In' | 'Cash Out'
type Currency = 'VND' | 'USD'

export interface BankCashInRow {
  date?: string | null
  bank?: string | null
  cashIn?: number | null
  currencyIn?: string | null
  cashOut?: number | null
  currencyOut?: string | null
  fx?: number | null
}

interface Props {
  open: boolean
  bank: BankName
  /** null → tạo mới; có giá trị → edit (date không đổi, chỉ side có giá trị mới enable). */
  row: BankCashInRow | null
  onClose: () => void
  /** Callback sau khi save thành công (parent thường reset selection). */
  onSaved: () => void
  /** Permission gating (Layer 4) — false → ẩn Save + read-only banner. */
  canEdit?: boolean
}

const defaultCurrencyFor = (bank: BankName): Currency => (bank === 'BIDV' ? 'USD' : 'VND')

const numFromStr = (s: string): number | null => {
  const t = s.trim()
  if (t === '') return null
  const n = Number(t.replace(/,/g, ''))
  return Number.isFinite(n) ? n : null
}

const strFromNum = (n: number | null | undefined): string => (n == null ? '' : String(n))

export default function BankCashInDialog({ open, bank, row, onClose, onSaved, canEdit = true }: Props) {
  const snack = useSnackbar()
  const [upsert, upsertState] = useUpsertBankCashMutation()
  const isEdit = row != null

  const initialTypeOfCash: TypeOfCash = useMemo(() => {
    if (!row) return 'Cash In'
    return row.cashIn != null && row.cashIn !== 0 ? 'Cash In' : 'Cash Out'
  }, [row])

  const [dateStr, setDateStr] = useState('')
  const [typeOfCash, setTypeOfCash] = useState<TypeOfCash>('Cash In')
  const [cashIn, setCashIn] = useState('')
  const [currencyIn, setCurrencyIn] = useState<Currency>('VND')
  const [cashOut, setCashOut] = useState('')
  const [currencyOut, setCurrencyOut] = useState<Currency>('VND')
  const [fx, setFx] = useState('')
  const [touched, setTouched] = useState(false)

  // Reset state mỗi khi mở dialog hoặc đổi row.
  useEffect(() => {
    if (!open) return
    setTouched(false)
    if (row) {
      setDateStr(String(row.date ?? '').slice(0, 10))
      setTypeOfCash(initialTypeOfCash)
      setCashIn(strFromNum(row.cashIn))
      setCurrencyIn(((row.currencyIn as Currency) ?? defaultCurrencyFor(bank)) || 'VND')
      setCashOut(strFromNum(row.cashOut))
      setCurrencyOut(((row.currencyOut as Currency) ?? defaultCurrencyFor(bank)) || 'VND')
      setFx(strFromNum(row.fx))
    } else {
      const def = defaultCurrencyFor(bank)
      const now = new Date()
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
      setDateStr(today)
      setTypeOfCash('Cash In')
      setCashIn('')
      setCurrencyIn(def)
      setCashOut('')
      setCurrencyOut(def)
      setFx('')
    }
  }, [open, row, bank, initialTypeOfCash])

  // type_of_cash_AfterUpdate (Access): khi đổi type → enable đúng nhánh, clear giá trị bên kia.
  // Ở edit mode KHÔNG cho đổi type (Access cũng disable theo nhánh hiện có).
  const handleTypeChange = (next: TypeOfCash) => {
    if (isEdit) return
    setTypeOfCash(next)
    if (next === 'Cash In') {
      setCashOut('')
    } else {
      setCashIn('')
    }
  }

  const cashInEnabled = typeOfCash === 'Cash In'
  const cashOutEnabled = typeOfCash === 'Cash Out'

  const canSave = canEdit && dateStr.trim() !== '' && !upsertState.isLoading
  const titleAction = isEdit ? 'Edit' : 'Add'

  const onSave = async () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    setTouched(true)
    if (!dateStr) {
      snack.error('Missing date')
      return
    }
    try {
      await upsert({
        date: dateStr,
        bank,
        // Chỉ gửi side đang active để không ghi đè 0 vào bên không liên quan.
        cashIn: cashInEnabled ? numFromStr(cashIn) : null,
        currencyIn: cashInEnabled ? currencyIn : currencyIn,
        cashOut: cashOutEnabled ? numFromStr(cashOut) : null,
        currencyOut: cashOutEnabled ? currencyOut : currencyOut,
        fx: numFromStr(fx),
      } as Record<string, unknown>).unwrap()
      snack.success(isEdit ? 'Successfully Updated' : 'Successfully Added')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(futuresApiErrorMessage(err))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={futuresSaleContractDialogPaperProps}
    >
      <DialogTitle sx={futuresFormDialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title={`Futures Control - Bank Cash-In (${titleAction})`}
          sx={futuresFormDialogTitleTextSx}
        >
          Futures Control - Bank Cash-In ({titleAction})
        </Typography>
        <Box sx={futuresFormDialogActionsBoxSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void onSave()}
            disabled={!canSave}
            sx={futuresFormDialogActionBtnSx}
          >
            {upsertState.isLoading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={upsertState.isLoading}
            sx={futuresFormDialogActionBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent dividers sx={futuresSaleContractDialogContentSx}>
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          {!canEdit ? (
            <Alert severity="info">
              Read-only — you don't have edit permission for this module.
            </Alert>
          ) : null}
          {/* Row 1: Bank + Date */}
          <Box sx={FORM_GRID_2COL}>
            <TextField label="Bank" value={bank} size="small" InputProps={{ readOnly: true }} fullWidth />
            <DatePicker
              label="Date *"
              format="DD-MMM-YYYY"
              value={dateStr ? dayjs(dateStr) : null}
              onChange={(d) => setDateStr(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              disabled={isEdit}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  InputLabelProps: { shrink: true },
                  error: touched && !dateStr,
                  helperText: touched && !dateStr ? 'Please choose Date' : isEdit ? 'Cannot change in edit mode' : '',
                },
              }}
            />
          </Box>

          {/* Row 2: Type of Cash */}
          <Box sx={FORM_GRID_2COL}>
            <Autocomplete
              size="small"
              autoHighlight
              options={['Cash In', 'Cash Out'] as TypeOfCash[]}
              value={typeOfCash}
              onChange={(_, v) => handleTypeChange((v as TypeOfCash) ?? 'Cash In')}
              isOptionEqualToValue={(opt, val) => opt === val}
              disabled={isEdit}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Type of Cash"
                  size="small"
                  fullWidth
                  helperText={isEdit ? 'Locked by source row' : ''}
                />
              )}
            />
            <Box />
          </Box>

          {/* Row 3: Cash In | Cash Out */}
          <Box sx={FORM_GRID_2COL}>
            <NumericInput
              label="Cash In"
              value={cashIn}
              onChange={setCashIn}
              size="small"
              fullWidth
              disabled={!cashInEnabled}
            />
            <NumericInput
              label="Cash Out"
              value={cashOut}
              onChange={setCashOut}
              size="small"
              fullWidth
              disabled={!cashOutEnabled}
            />
          </Box>

          {/* Row 4: Currency In | Currency Out */}
          <Box sx={FORM_GRID_2COL}>
            <Autocomplete
              size="small"
              autoHighlight
              options={['VND', 'USD'] as Currency[]}
              value={currencyIn}
              onChange={(_, v) => setCurrencyIn((v as Currency) ?? 'VND')}
              isOptionEqualToValue={(opt, val) => opt === val}
              disableClearable
              renderInput={(params) => <TextField {...params} label="Currency In" size="small" fullWidth />}
            />
            <Autocomplete
              size="small"
              autoHighlight
              options={['VND', 'USD'] as Currency[]}
              value={currencyOut}
              onChange={(_, v) => setCurrencyOut((v as Currency) ?? 'VND')}
              isOptionEqualToValue={(opt, val) => opt === val}
              disableClearable
              renderInput={(params) => <TextField {...params} label="Currency Out" size="small" fullWidth />}
            />
          </Box>

          {/* Row 5: FX */}
          <Box sx={FORM_GRID_2COL}>
            <NumericInput
              label="FX"
              value={fx}
              onChange={setFx}
              size="small"
              fullWidth
            />
            <Box />
          </Box>

          <Typography variant="caption" color="text.secondary">
            
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
